import { type FC, useMemo } from "react";
import { Dialog } from "@mui/material";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import {
  useCountryAccessQuery,
  useUpdateCountryAccessMutation,
} from "@/partner/entities/countryAccesses";
import {
  mapCountryAccessToFormValues,
  mapFormValuesToUpdateCountryAccessInput,
  type CountryAccessFormValues,
} from "../model";
import { CountryAccessForm } from "./CountryAccessForm";

interface EditCountryAccessDialogProps {
  open: boolean;
  countryAccessId: string | null;
  onClose: () => void;
  onCompleted?: () => void;
}

export const EditCountryAccessDialog: FC<EditCountryAccessDialogProps> = ({
  open,
  countryAccessId,
  onClose,
  onCompleted,
}) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  const { data, loading } = useCountryAccessQuery({
    variables: { id: String(countryAccessId) },
    skip: !countryAccessId || !open,
  });

  const [updateCountryAccess, { loading: updating }] =
    useUpdateCountryAccessMutation({
      onCompleted: () => {
        enqueueSnackbar(t("countryAccesses.edit.success"), {
          variant: "success",
        });
      },
      onError: (error) => {
        enqueueSnackbar(error.message, { variant: "error" });
      },
      refetchQueries: ["CountryAccessesList", "CountryAccessesSelect"],
    });

  const initialValues = useMemo(
    () => mapCountryAccessToFormValues(data?.countryAccess),
    [data]
  );

  const handleSubmit = async (values: CountryAccessFormValues) => {
    await updateCountryAccess({
      variables: {
        id: String(countryAccessId),
        input: mapFormValuesToUpdateCountryAccessInput(values),
      },
    });

    onCompleted?.();
  };

  return (
    <Dialog
      fullScreen
      maxWidth="md"
      scroll="body"
      open={open}
      onClose={onClose}
    >
      <CountryAccessForm
        initialValues={initialValues}
        onSubmit={handleSubmit}
        onClose={onClose}
        title={t("countryAccesses.edit.title")}
        loading={loading || updating}
      />
    </Dialog>
  );
};
